import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { Button } from "@/components/ui/button";
import { ArrowRight, User, Building2, Sparkles } from "lucide-react";
import { INDIVIDUAL_FEATURES, ENTERPRISE_FEATURES } from "@/lib/content";

function FeatureGrid({ items, dark, prefix }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      {items.map((f, i) => (
        <motion.div key={f.title} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ delay: i * 0.08, duration: 0.45 }} data-testid={`${prefix}-feature-${i}`}
          className={`rounded-xl border p-7 hover:-translate-y-1 transition-transform ${dark ? "border-white/10 bg-white/5" : "border-[#E8ECF2] bg-white"}`}>
          <div className={`h-11 w-11 rounded-md flex items-center justify-center ${dark ? "bg-[#7DE3B4] text-[#1A3A5C]" : "bg-[#1A3A5C] text-white"}`}><f.icon size={18} /></div>
          <h3 className={`mt-5 font-display text-xl font-bold ${dark ? "text-white" : "text-[#1A1A2E]"}`}>{f.title}</h3>
          <p className={`mt-3 text-sm leading-relaxed ${dark ? "text-[#E8ECF2]/80" : "text-[#555568]"}`}>{f.body}</p>
        </motion.div>
      ))}
    </div>
  );
}

export default function Features() {
  return (
    <MarketingLayout>
      <section className="pt-28 pb-16 max-w-6xl mx-auto px-6 lg:px-10" data-testid="features-hero">
        <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-4">Features</p>
        <h1 className="font-display text-5xl lg:text-7xl tracking-tighter text-[#1A1A2E] leading-[0.95] max-w-3xl">
          Everything WYLO does<br /><span className="text-[#1A3A5C]">to bring you back.</span>
        </h1>
        <p className="mt-7 text-lg text-[#555568] leading-relaxed max-w-2xl">
          One product, two audiences. Individuals get a calm return to the work that matters. Organisations get an honest,
          anonymised picture of where focus is won and lost. Nobody gets watched.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="#individuals" data-testid="features-jump-individuals">
            <Button variant="outline" className="border-[#1A3A5C] text-[#1A1A2E] h-11 px-6"><User size={16} className="mr-2" /> For individuals</Button>
          </a>
          <a href="#enterprise" data-testid="features-jump-enterprise">
            <Button variant="outline" className="border-[#1A3A5C] text-[#1A1A2E] h-11 px-6"><Building2 size={16} className="mr-2" /> For enterprise</Button>
          </a>
        </div>
      </section>

      <section id="individuals" className="scroll-mt-24 bg-white border-y border-[#E8ECF2]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-4 inline-flex items-center gap-2"><User size={14} /> For individuals</p>
          <h2 className="font-display text-3xl lg:text-4xl tracking-tighter text-[#1A1A2E] mb-10">Your place, held. Your data, yours.</h2>
          <FeatureGrid items={INDIVIDUAL_FEATURES} prefix="individual" />
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/individuals" data-testid="features-cta-individuals"><Button variant="outline" className="border-[#1A3A5C] h-11 px-6">Who it's for</Button></Link>
            <Link to="/register" data-testid="features-cta-trial"><Button className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white h-11 px-6">Start Free Trial <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
          </div>
        </div>
      </section>

      <section id="enterprise" className="scroll-mt-24 bg-[#1A3A5C] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <p className="text-xs uppercase tracking-[0.2em] text-[#7DE3B4] font-semibold mb-4 inline-flex items-center gap-2"><Building2 size={14} /> For enterprise</p>
          <h2 className="font-display text-3xl lg:text-4xl tracking-tighter mb-10">See the whole organisation without watching anyone.</h2>
          <FeatureGrid items={ENTERPRISE_FEATURES} dark prefix="enterprise" />
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/enterprise" data-testid="features-cta-enterprise"><Button className="bg-white text-[#1A3A5C] hover:bg-[#E8ECF2] h-11 px-6">Explore Enterprise</Button></Link>
            <Link to="/pricing" data-testid="features-cta-pricing"><Button variant="outline" className="border-white/40 text-white hover:bg-white/10 h-11 px-6">Compare tiers <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-20">
        <div className="rounded-2xl border border-[#E8ECF2] bg-white p-8 lg:p-10 flex flex-col md:flex-row md:items-center gap-6">
          <div className="h-14 w-14 shrink-0 rounded-xl bg-[#BA7517]/10 text-[#BA7517] flex items-center justify-center"><Sparkles size={26} /></div>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-1">Add-on · Compass</p>
            <h3 className="font-display text-2xl font-bold text-[#1A1A2E]">Real-time AI insights and assistance.</h3>
            <p className="mt-2 text-sm text-[#555568] leading-relaxed">Behavioural modelling, AI-enhanced reports and an AI support chatbot. Included with Command; a paid add-on for every other tier.</p>
          </div>
          <Link to="/pricing" data-testid="features-cta-compass"><Button variant="outline" className="border-[#1A3A5C] h-11 px-6">See pricing</Button></Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
